/**
 * Guest identity for video calls: a client-generated `did:key`.
 *
 * A logged-out visitor needs a cryptographically-real identity to join a call
 * (the calls worker verifies a signed service-auth JWT), but we don't want a
 * signup. So on first use we generate a Secp256k1 keypair in the browser and
 * derive a `did:key:z...` whose suffix IS the public key. It's self-certifying:
 * the worker verifies our signed tokens with no network resolution and no
 * account. The private key is persisted in localStorage so the same guest DID
 * resumes across reloads (device-local by design; clearing storage = new DID).
 *
 * A logged-in atproto user does NOT use this -- their real DID + PDS-signed
 * service-auth is preferred (see callToken.ts). When a guest later logs in, we
 * record the guest->real mapping so callers can carry context forward.
 *
 * Web-only: this file relies on browser crypto + localStorage. The native build
 * gets guestIdentity.native.ts (a stub), since the call feature is web-only.
 */

import {Secp256k1Keypair} from '@atproto/crypto'

const GUEST_KEY_STORAGE = 'bate.calls.guestKey.v1'
const GUEST_MAP_STORAGE = 'bate.calls.guestUpgradeMap.v1'

export interface CallIdentity {
  /** The caller's DID (guest did:key, here). */
  did: string
  /**
   * Signs the service-JWT signing input (`header.payload` bytes) and returns
   * the base64url signature. Mirrors what a PDS getServiceAuth does for real
   * users, but locally with the guest keypair.
   */
  signJwtInput: (signingInput: string) => Promise<string>
  /** JWT `alg` for this key (ES256K for Secp256k1). */
  alg: string
}

let cached: {did: string; keypair: Secp256k1Keypair} | null = null

function toBase64Url(bytes: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

function fromHex(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return out
}

/**
 * Loads the persisted guest keypair, or generates + persists a new one.
 * Cached in-memory for the session so we don't re-import on every sign.
 */
async function loadOrCreateGuestKeypair(): Promise<{
  did: string
  keypair: Secp256k1Keypair
}> {
  if (cached) return cached

  const stored = safeGetItem(GUEST_KEY_STORAGE)
  if (stored) {
    try {
      const keypair = await Secp256k1Keypair.import(fromHex(stored), {
        exportable: true,
      })
      cached = {did: keypair.did(), keypair}
      return cached
    } catch {
      // Corrupt stored key -- fall through and mint a fresh one.
    }
  }

  const keypair = await Secp256k1Keypair.create({exportable: true})
  const exported = await keypair.export()
  safeSetItem(GUEST_KEY_STORAGE, toHex(exported))
  cached = {did: keypair.did(), keypair}
  return cached
}

/**
 * Returns a CallIdentity backed by the persisted guest did:key. Generates one
 * on first call.
 */
export async function getGuestIdentity(): Promise<CallIdentity> {
  const {did, keypair} = await loadOrCreateGuestKeypair()
  return {
    did,
    alg: 'ES256K',
    signJwtInput: async (signingInput: string) => {
      const sig = await keypair.sign(new TextEncoder().encode(signingInput))
      return toBase64Url(sig)
    },
  }
}

/**
 * The current guest DID if one has been created, else null (no side effects).
 */
export function peekGuestDid(): string | null {
  if (cached) return cached.did
  return null
}

/**
 * Records that this guest has upgraded to a real atproto account, so callers
 * can carry forward context (e.g. "you were previously guest X"). Client-side
 * only; the server does not merge history.
 */
export function recordGuestUpgrade(guestDid: string, realDid: string): void {
  try {
    const raw = safeGetItem(GUEST_MAP_STORAGE)
    const map: Record<string, string> = raw
      ? (JSON.parse(raw) as Record<string, string>)
      : {}
    map[guestDid] = realDid
    safeSetItem(GUEST_MAP_STORAGE, JSON.stringify(map))
  } catch {
    // best-effort
  }
}

/**
 * Looks up the real DID a guest upgraded to, if any.
 */
export function getUpgradedDid(guestDid: string): string | null {
  try {
    const raw = safeGetItem(GUEST_MAP_STORAGE)
    if (!raw) return null
    const map = JSON.parse(raw) as Record<string, unknown>
    const val = map[guestDid]
    return typeof val === 'string' ? val : null
  } catch {
    return null
  }
}

function safeGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // storage unavailable (private mode quota, etc.) -- identity becomes
    // session-only, which is acceptable for a guest.
  }
}
